import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import SessionService from '../services/SessionService';
import { updateUserThemePreference, getUserThemePreference } from '../services/UserService';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: ThemeMode;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: ThemeMode) => Promise<void>;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = '@ligchat/themePreference';

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getInitialTheme = (): ThemeMode => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
      return saved && ['light', 'dark', 'system'].includes(saved) ? saved : 'dark';
    } catch {
      return 'dark';
    }
  };

  const getInitialResolved = (): 'light' | 'dark' => {
    const mode = getInitialTheme();
    if (mode === 'light') return 'light';
    if (mode === 'dark') return 'dark';
    return getSystemTheme();
  };

  const [theme, setThemeState] = useState<ThemeMode>(getInitialTheme);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(getInitialResolved);

  // Aplicar tema imediatamente na montagem para evitar flash
  useEffect(() => {
    const resolved = theme === 'system' ? getSystemTheme() : theme;
    document.documentElement.setAttribute('data-theme', resolved);
  }, []);

  const applyTheme = useCallback((resolved: 'light' | 'dark') => {
    document.documentElement.setAttribute('data-theme', resolved);
    setResolvedTheme(resolved);
  }, []);

  const setTheme = useCallback(async (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);

    const resolved = newTheme === 'system' ? getSystemTheme() : newTheme;
    applyTheme(resolved);

    const token = SessionService.getToken();
    if (token) {
      try {
        await updateUserThemePreference(newTheme);
      } catch (err) {
        console.warn('[Theme] Falha ao salvar preferência no servidor:', err);
      }
    }
  }, [applyTheme]);

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setTheme]);

  useEffect(() => {
    applyTheme(theme === 'system' ? getSystemTheme() : theme);
  }, [theme, applyTheme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => {
      if (theme === 'system') {
        applyTheme(getSystemTheme());
      }
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [theme, applyTheme]);

  useEffect(() => {
    const loadFromServer = async () => {
      const token = SessionService.getToken();
      if (!token) return;

      try {
        const serverTheme = await getUserThemePreference();
        if (serverTheme) {
          setThemeState(serverTheme);
          localStorage.setItem(STORAGE_KEY, serverTheme);
          const resolved = serverTheme === 'system' ? getSystemTheme() : serverTheme;
          applyTheme(resolved);
        }
      } catch {
        // Usar preferência local se API falhar
      }
    };

    loadFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};
